import type { TDocumentDefinitions, Content, TableCell } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import type { PmDocumentModel } from "../../../domain/ndt/pm-report-domain.js";
import {
  GRID_LAYOUT,
  KIT_COLORS,
  KIT_PAGE_MARGINS,
  LAUDO_FILL,
  buildCroquiBox,
  buildFooter,
  buildHeader,
  buildIdentification,
  buildLegend,
  buildPhotoBlocks,
  buildSignatories,
  buildTextBox,
  kitStyles,
  pageBreakBefore,
  pairGrid,
  withDocumentVisual,
  sectionBand,
  th,
  value
} from "./ndt-report-kit.js";

/**
 * RELATÓRIO DE ENSAIO POR PARTÍCULAS MAGNÉTICAS — FORM-PM REV.00.
 * Seções e campos exatamente como o modelo; paginação dinâmica (nenhum
 * pageBreak forçado). Consome o PmDocumentModel — o mesmo objeto do Preview.
 */
export class PmBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    // O visual do Studio congelado no modelo vale durante toda a montagem (e só nela).
    return withDocumentVisual(((snapshot.data || {}) as PmDocumentModel).visual ?? null, () => this.compose(snapshot));
  }

  private compose(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const model = (snapshot.data || {}) as PmDocumentModel;
    return {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: KIT_PAGE_MARGINS,
      header: () => buildHeader(model, "RELATÓRIO DE ENSAIO POR\nPARTÍCULAS MAGNÉTICAS", "PM"),
      pageBreakBefore,
      footer: buildFooter(model, "FORM-PM", "Relatório de Ensaio por Partículas Magnéticas"),
      content: [
        sectionBand("01", "IDENTIFICAÇÃO"),
        buildIdentification(model),
        sectionBand("02", "DADOS DO ENSAIO"),
        this.buildTestData(model),
        sectionBand("03", "EQUIPAMENTO E CONSUMÍVEIS"),
        this.buildEquipment(model),
        this.buildConsumables(model),
        sectionBand("04", "REGISTRO DA INSPEÇÃO / RESULTADOS"),
        this.buildResults(model),
        sectionBand("05", "CROQUI"),
        buildCroquiBox(model.croqui),
        sectionBand("06", "OBSERVAÇÕES E PARECER FINAL"),
        buildTextBox(model.observations),
        sectionBand("07", "LEGENDA E CRITÉRIO DE RESULTADO"),
        buildLegend(model),
        sectionBand("08", "EVIDÊNCIAS FOTOGRÁFICAS"),
        buildPhotoBlocks(model.photos),
        sectionBand("09", "TERMO DE RESPONSABILIDADE & SIGNATÁRIOS"),
        buildSignatories(model)
      ],
      styles: kitStyles(),
      defaultStyle: { fontSize: 7.5, color: KIT_COLORS.text }
    };
  }

  private buildTestData(model: PmDocumentModel): Content {
    const t = model.testData || ({} as PmDocumentModel["testData"]);
    return pairGrid(
      [
        ["Procedimento / Revisão:", t.procedure],
        ["Critério de Aceitação:", t.acceptanceCriteria],
        ["Condição Superficial:", t.surfaceCondition],
        ["Método de Magnetização:", t.magnetizationMethod],
        ["Direção / Sentido:", t.magnetizationDirection],
        ["Tipo de Iluminação:", t.illuminationType],
        ["Temperatura do Ensaio:", t.testTemperature]
      ],
      [
        ["Norma de Referência:", t.referenceNorm],
        ["Material Base:", t.baseMaterial],
        ["Técnica / Aplicação:", t.technique],
        ["Tipo de Corrente:", t.currentType],
        ["Distância entre Polos:", t.poleSpacing],
        ["Intensidade Medida:", t.illuminationIntensity],
        ["Observação Técnica:", t.technicalNote]
      ]
    );
  }

  private buildEquipment(model: PmDocumentModel): Content {
    const e = model.equipment || ({} as PmDocumentModel["equipment"]);
    return pairGrid(
      [["Yoke / Equipamento:", e.yoke], ["Nº de Série / Identificação:", e.serialNumber]],
      [["Certificado / Validade:", e.certificate], ["Verificação Funcional:", e.functionalCheck]]
    );
  }

  /** Tabela TIPO · MARCA · PRODUTO · LOTE · VALIDADE · TÉCNICA/APLICAÇÃO do modelo. */
  private buildConsumables(model: PmDocumentModel): Content {
    const list = Array.isArray(model.consumables) ? model.consumables : [];
    const body: TableCell[][] = [[th("Tipo"), th("Marca"), th("Produto"), th("Lote"), th("Validade"), th("Técnica / Aplicação")]];
    if (list.length === 0) {
      body.push([{ text: "—", style: "empty", colSpan: 6, alignment: "center" }, {}, {}, {}, {}, {}]);
    } else {
      for (const c of list) {
        body.push([
          { text: value(c.typeLabel), style: "td" },
          { text: value(c.brand), style: "td" },
          { text: value(c.product), style: "td" },
          { text: value(c.lot), style: "td" },
          { text: value(c.validity), style: "td", color: c.validity ? (c.expired ? "#9B1C1C" : "#1E6B34") : KIT_COLORS.text, bold: !!c.validity },
          { text: value(c.technique), style: "td" }
        ]);
      }
    }
    return { margin: [0, 6, 0, 0], table: { headerRows: 1, widths: [96, 90, 96, 60, 56, "*"], body, dontBreakRows: true }, layout: GRID_LAYOUT };
  }

  /** Nº · JUNTA/ELEMENTO · LOCALIZAÇÃO · EXTENSÃO · INDICAÇÕES/ACHADOS · LAUDO. */
  private buildResults(model: PmDocumentModel): Content {
    const rows = Array.isArray(model.results) ? model.results : [];
    const body: TableCell[][] = [[th("Nº", "center"), th("Junta / Elemento"), th("Localização"), th("Extensão", "center"), th("Indicações / Achados"), th("Laudo", "center")]];
    if (rows.length === 0) {
      body.push([{ text: "Nenhum item registrado.", style: "empty", colSpan: 6, alignment: "center" }, {}, {}, {}, {}, {}]);
    } else {
      for (const r of rows) {
        const paint = LAUDO_FILL[r.laudo] ?? null;
        body.push([
          { text: value(r.item), style: "td", alignment: "center" },
          { text: value(r.element), style: "td" },
          { text: value(r.location), style: "td" },
          { text: value(r.extension), style: "td", alignment: "center" },
          { text: value(r.findings), style: "td" },
          paint ? { text: value(r.laudoLabel), style: "td", alignment: "center", bold: true, fillColor: paint.fill, color: paint.color } : { text: value(r.laudoLabel), style: "td", alignment: "center" }
        ]);
      }
    }
    return { table: { headerRows: 1, widths: [26, 84, 84, 52, "*", 84], body, dontBreakRows: true }, layout: GRID_LAYOUT };
  }
}

documentEngine.registerBuilder("PM", new PmBuilder());
